import {
  action, observable,
} from 'mobx';
import Connections from '../../connections';
import {
  createBranch, getBranchById, getBranches, getWDCBranches, updateBranch, updateBranchStatus, getBranchesRating,
} from '../../axios/branchesRequests';
import {
  uploadFileURL,
} from '../../axios/requests';
import {
  convertCountriesToISOCodeString,
} from '../../constants/countries';
import {
  DefaultLanguage,
} from '../../constants/languages';
import {
  getHeader,
} from '../../helperFunctions/getHeader';
import {
  uploadFile,
} from '../../helperFunctions/uploadService';
import {
  ICountry,
} from '../storeInterfaces/authInterface';
import {
  Branch, IBranchStore, Image, BranchRating,
  MediaFile, BranchDTO, MediaTypes, Image360, Video,
} from '../storeInterfaces/branchInterface';
import { RequestStatus } from 'mobx-store';

export class BranchStore implements IBranchStore {
  defaultStatus = 'all';

  @observable branchesList: Branch[] = [];

  @observable wdcBranchesList: any[] = [];

  @observable selectedBranch = new Branch();

  @observable loading = false;

  @observable branchesRating: BranchRating[] = [];

  @observable getBranchesRequestStatus: RequestStatus = RequestStatus.IDLE;

  @action
  public async getBranches(countries: ICountry[], langCode: string = DefaultLanguage, status: string = this.defaultStatus): Promise<any> {
    try {
      this.loading = true;
      this.getBranchesRequestStatus = RequestStatus.LOADING;
      const availableCountries = convertCountriesToISOCodeString(countries);
      const params = {
        availableCountries,
        status,
        branchType: 'all', // fetch all branches. other options: 'club' or 'waterfront'
      };
      const config = {
        ...getHeader(langCode), params,
      };

      const response = await Connections.wadiDeglaAxios.httpGet(getBranches, config);
      this.branchesList = this.restructureBranchesList(response.data);
      this.getBranchesRequestStatus = RequestStatus.SUCCESS;
    } catch (error) {
      this.getBranchesRequestStatus = RequestStatus.FAILED;
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async getWDCBranches(langCode: string = DefaultLanguage, selectedWdcBranch: number): Promise<any> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getWDCBranches(selectedWdcBranch), getHeader(langCode));
      this.wdcBranchesList = this.restructureWDCBranchesList(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async getBranchesRating(countries: ICountry[]): Promise<any> {
    try {
      this.loading = true;
      this.getBranchesRequestStatus = RequestStatus.LOADING;
      const availableCountries = convertCountriesToISOCodeString(countries);
      const response = await Connections.wadiDeglaAxios.httpGet(getBranchesRating, {
        params: {
          availableCountries
        }
      });
      this.branchesRating = response.data;
      this.getBranchesRequestStatus = RequestStatus.SUCCESS;
    } catch (error) {
      this.getBranchesRequestStatus = RequestStatus.FAILED;
      console.log(error);
      Promise.reject(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async filterBranches(countries: ICountry[], query: string, langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      const availableCountries = convertCountriesToISOCodeString(countries);

      const config = {
        ...getHeader(langCode),
        params: {
          query,
          availableCountries,
        },
      };
      const response = await Connections.wadiDeglaAxios.httpGet(getBranches, config);
      this.branchesList = this.restructureBranchesList(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getBranchById(id: number, langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getBranchById(id), getHeader(langCode));
      this.selectedBranch = this.restructureBranch(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createBranch(newBranch: Branch, langCode: string = DefaultLanguage): Promise<any> {
    return new Promise<void>(async (res, rej) => {
      try {
        const allImages = await this.uploadBranchMediaFiles(newBranch.getImages());
        const allVideos = await this.uploadBranchMediaFiles(newBranch.getVideos());
        const allImages360 = await this.uploadBranchMediaFiles(newBranch.getImages360());
        const backgroundImage = await this.uploadBranchMediaFiles(newBranch.getBackgroundImage());
        const masterPlanImage = await this.uploadBranchMediaFiles(newBranch.getMasterPlanImage());
        newBranch.setImages(allImages);
        newBranch.setVideos(allVideos);
        newBranch.setImages360(allImages360);
        newBranch.setBackgroundImage(backgroundImage);
        newBranch.setMasterPlanImage(masterPlanImage);
        const response = await Connections.wadiDeglaAxios.httpPost(createBranch, this.restructureCreateBranch(newBranch, langCode));
        res(response.data);
      } catch (error) {
        rej(error);
      }
    });
  }

  @action.bound
  public async updateBranch(updatedBranch: Branch, id: number, initImagesGallery: Image[], initVideosGallery: Video[],
    initImages360Gallery: Image360[], langCode: string = DefaultLanguage): Promise<any> {
    try {
      const {
        newlyAdded: newImages,
        deletedIds: deletedImagesIds,
      } = this.mapNewAndDeletedFromGallery(initImagesGallery, updatedBranch.getImages());
      const {
        newlyAdded: newVideos,
        deletedIds: deletedVideosIds,
      } = this.mapNewAndDeletedFromGallery(initVideosGallery, updatedBranch.getVideos());
      const {
        newlyAdded: newImages360,
        deletedIds: deletedImages360Ids,
      } = this.mapNewAndDeletedFromGallery(initImages360Gallery, updatedBranch.getImages360());

      const uploadedImages = await this.uploadBranchMediaFiles(newImages);
      const uploadedVideos = await this.uploadBranchMediaFiles(newVideos);
      const uploadedImages360 = await this.uploadBranchMediaFiles(newImages360);
      const backgroundImage = await this.uploadBranchMediaFiles(updatedBranch.getBackgroundImage());
      const masterPlanImage = await this.uploadBranchMediaFiles(updatedBranch.getMasterPlanImage());
      updatedBranch.setBackgroundImage(backgroundImage);
      updatedBranch.setMasterPlanImage(masterPlanImage);
      await Connections.wadiDeglaAxios.httpPut(updateBranch(id),
        this.restructureUpdatedBranch(
          updatedBranch, uploadedImages, deletedImagesIds, uploadedVideos, deletedVideosIds,
          uploadedImages360, deletedImages360Ids, langCode,
        ));
    } catch (error) {
      console.error(error);
    }
  }

  private mapNewAndDeletedFromGallery(initGallery: any[], submittedFiles: any[]): {
    newlyAdded: string[], deletedIds: number[],
  } {
    const deletedIds: number[] = [];
    const newlyAdded: any[] = [];
    submittedFiles.forEach((file) => {
      if (file.preview) {
        newlyAdded.push(file);
      } else {
        const findIndex = initGallery.findIndex((media) => media.key === file.key);
        if (findIndex !== -1) {
          initGallery.splice(findIndex, 1);
        }
      }
    });
    initGallery.forEach((item) => deletedIds.push(item.key));
    return {
      newlyAdded,
      deletedIds,
    };
  }

  @action.bound
  public async updateBranchStatus(updatedBranchStatus: boolean, index: number): Promise<any> {
    try {
      const response = await Connections.wadiDeglaAxios.httpPut(updateBranchStatus(this.branchesList[index].getId()), {
        isActive: updatedBranchStatus,
      });
      this.branchesList[index].setIsActive(updatedBranchStatus);
      this.branchesList = this.branchesList.slice();
    } catch (error) {
      console.log(error);
    }
  }

  private restructureBranchesList(branches: any[]): Branch[] {
    const branchesList: Branch[] = [];
    for (const branch of branches) {
      const branchData = new Branch();
      branchData.setId(branch.branchId);
      branchData.setTitle(branch.title);
      branchData.setIsActive(branch.isActive);
      branchesList.push(branchData);
    }
    return branchesList;
  }

  private restructureWDCBranchesList(branches: any[]): any[] {
    const branchesList = [];
    for (const branch of branches) {
      branchesList.push({
        wcdBranchId: branch.wcdBranchId,
        title: branch.title,
      });
    }
    return branchesList;
  }

  private restructureBranch(branch: BranchDTO): Branch {
    const restructuredBranch: Branch = new Branch();
    restructuredBranch.setId(branch.branchId);
    restructuredBranch.setTitle(branch.title);
    restructuredBranch.setIsWaterfront(!!(branch.waterfrontBranch && branch.waterfrontBranch.wfBranchId));
    restructuredBranch.setDescription(branch.description);
    if (branch.branchImages && branch.branchImages.length) {
      restructuredBranch.setImages(
        branch.branchImages.map((image: any) => ({
          imageUrl: image.imageURL,
          key: image.branchImageId,
          type: MediaTypes.Image,
        })),
      );
    }
    if (branch.media && branch.media.length) {
      restructuredBranch.setVideos(
        branch.media.filter((mediaFile: MediaFile) => mediaFile.mediaType === MediaTypes.Video)
          .map((mediaFile: MediaFile) => ({
            url: mediaFile.url,
            key: mediaFile.mediaId,
            type: mediaFile.mediaType,
          })),
      );
      restructuredBranch.setImages360(
        branch.media.filter((mediaFile: MediaFile) => mediaFile.mediaType === MediaTypes.Image360)
          .map((mediaFile: MediaFile) => ({
            url: mediaFile.url,
            key: mediaFile.mediaId,
            type: mediaFile.mediaType,
          })),
      );
    }
    if (branch.backgroundImageURL) {
      restructuredBranch.setBackgroundImage([{
        imageUrl: branch.backgroundImageURL, key: 1,
      }]);
    }
    if (branch.masterPlanImageURL) {
      restructuredBranch.setMasterPlanImage([{
        imageUrl: branch.masterPlanImageURL, key: 1,
      }]);
    }
    restructuredBranch.setLocation({
      latitude: Number(branch.latitude),
      longitude: Number(branch.longitude),
    });
    restructuredBranch.setWdcBranch(branch.wdcBranchId);
    restructuredBranch.setFeatures(branch.features.map((feature) => feature.branchFeatureId));
    return restructuredBranch;
  }

  private uploadBranchMediaFiles(mediaFiles: any[]) {
    const promises: Promise<any>[] = [];
    const uploadedLinks: any[] = [];
    mediaFiles.forEach((file) => {
      if (file.preview) {
        promises.push(
          uploadFile(uploadFileURL, file)
            .then((res: any) => {
              uploadedLinks.push({
                imageUrl: res.data.downloadURL,
              });
            }),
        );
      } else {
        uploadedLinks.push(file);
      }
    });
    return Promise.all(promises)
      .then(() => {
        return uploadedLinks;
      }).catch((error) => {
        console.error('Error uploading media', error);
        return uploadedLinks;
      });
  }

  private restructureUpdatedBranch(
    branch: Branch, uploadedImages: any[], deletedImagesIds: number[], uploadedVideos: any[],
    deletedVideosIds: number[], uploadedImages360: any[], deletedImages360Ids: number[], languageCode: string,
  ): any {
    const backgroundImage = this.getFirstImageUrl(branch.getBackgroundImage() as Image[]);
    const masterPlanImage = this.getFirstImageUrl(branch.getMasterPlanImage() as Image[]);
    const newImages = uploadedImages.map((image: any) => (image.imageUrl));
    const newVideos = uploadedVideos.map((video: any) => (video.imageUrl));
    const newImages360 = uploadedImages360.map((image: any) => (image.imageUrl));
    const restructuredBranch = {
      languageCode,
      title: branch.getTitle(),
      description: branch.getDescription(),
      isWaterfront: branch.getIsWaterfront(),
      longitude: branch.getLocation().longitude,
      latitude: branch.getLocation().latitude,
      wdcBranchId: branch.getWdcBranch(),
      backgroundImageURL: backgroundImage,
      masterPlanImageURL: masterPlanImage,
      deletedImagesIds,
      newImages,
      deletedVideosIds,
      newVideos,
      deletedImages360Ids,
      newImages360,
      availableCountries: branch.availableCountries,
      featuresIds: branch.getFeatures(),
    };
    return restructuredBranch;
  }

  private restructureCreateBranch(branch: Branch, languageCode: string): any {
    const backgroundImage = this.getFirstImageUrl(branch.getBackgroundImage() as Image[]);
    const masterPlanImage = this.getFirstImageUrl(branch.getMasterPlanImage() as Image[]);
    const images = branch.getImages().map((image: any) => (image.imageUrl));
    const videos = branch.getVideos().map((video: any) => (video.imageUrl));
    const images360 = branch.getImages360().map((image: any) => (image.imageUrl));
    const restructuredBranch = {
      languageCode,
      title: branch.getTitle(),
      description: branch.getDescription(),
      isWaterfront: branch.getIsWaterfront(),
      longitude: branch.getLocation().longitude,
      latitude: branch.getLocation().latitude,
      isActive: true,
      wdcBranchId: branch.getWdcBranch(),
      backgroundImageURL: backgroundImage,
      masterPlanImageURL: masterPlanImage,
      images,
      videos,
      images360,
      availableCountries: branch.availableCountries,
      featuresIds: branch.getFeatures(),
    };
    return restructuredBranch;
  }

  private getFirstImageUrl = (images: Image[]) => {
    if (images && images.length) {
      return images[0].imageUrl;
    }
    return null;
  };
}
